import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import propertyProposalService from "../services/propertyProposalService";
import propertiesService from "../services/propertiesService";
import DateInput from "../components/DateInput";

import "../assets/propertyProposalCreate.css";

// Helper functions for date format conversion
const convertToDisplayFormat = (dateString) => {
  // Convert various date formats to DD/MM/YYYY for display
  if (!dateString) return '';

  // Already in DD/MM/YYYY format
  if (dateString.includes('/')) {
    return dateString;
  }

  // Handle ISO format (YYYY-MM-DDTHH:mm:ss.sssZ) or YYYY-MM-DD
  if (dateString.includes('-') || dateString.includes('T')) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
  }

  return dateString;
};

// Safely parse proposed_works - always returns an array
const parseProposedWorks = (value) => {
  if (!value) return [""];
  if (Array.isArray(value)) return value.length > 0 ? value : [""];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.length > 0 ? parsed : [""];
      return [""];
    } catch (e) {
      console.warn("Failed to parse proposed_works JSON:", e);
      return [""];
    }
  }
  return [""];
};

const PropertyProposalEdit = () => {
  const { propertyId, proposalId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    property_id: propertyId,
    status_id: 0,
    client: "",
    client_contact_id: "",
    address: "",
    property_address_1: "",
    property_address_2: "",
    property_address_3: "",
    property_town: "",
    property_postcode: "",
    proposal_date: "",
    account_manager: "",
    account_manager_sex: "",
    account_manager_email: "",
    council_tax_liability: "",
    full_security: "",
    proposed_works: [""],
    unusual_matters: "",
    sia_security_guards: "",
    sia_security_guards_description: "",
    caretakers: "",
    caretakers_description: "",
    reduce_empty_business_rates: "",
    reduce_empty_business_rates_description: "",
    amount_to_return: "",
    start_up_fees: "",
    min_contract_period: "",
    min_contract_period_word: "",
    asbestos: "",
    asbestos_free_of_charge: "",
    legionella: "",
    utilities_maintenance: "",
    utilities_gas_electric: "",
    utilities_water: "",
    council_tax: "",
    business_rates: "",
    waste_arrange: "",
    waste_costs: "",
    managing_the_property: "",
    initial_fit_out_works: "",
    npp_minimum_contract_period: "",
    npp_notice_period: "",
    npp_revenue_share: "",
    npp_public_liability: "",
    npp_employers_liability: "",
    npp_professional_idemnity: "",
    // New Liabilities fields
    npp_myglobe_subscription_rm: "",
    npp_myglobe_subscription_rp: "",
    npp_myglobe_subscription_cow: "",
    npp_management_fee_rm: "",
    npp_management_fee_rp: "",
    npp_management_fee_cow: "",
    npp_building_insurance_rm: "",
    npp_building_insurancee_rp: "",
    npp_building_insurance_cow: "",
    npp_public_liability_insurance_rm: "",
    npp_public_liability_insurance_rp: "",
    npp_public_liability_insurance_cow: "",
    npp_sia_security_rm: "",
    npp_sia_security_rp: "",
    npp_sia_security_cow: "",
    npp_catetaker_rm: "",
    npp_catetaker_rp: "",
    npp_catetaker_cow: "",
    npp_access_egress_rm: "",
    npp_access_egress_rp: "",
    npp_access_egress_cow: "",
    npp_monthly_inspections_rm: "",
    npp_monthly_inspections_rp: "",
    npp_monthly_inspections_cow: "",
    npp_weekly_inspections_rm: "",
    npp_weekly_inspections_rp: "",
    npp_weekly_inspections_cow: "",
    npp_hmo_liscense_rm: "",
    npp_hmo_liscense_rp: "",
    npp_hmo_liscense_cow: "",
    npp_selective_license_rm: "",
    npp_selective_license_rp: "",
    npp_selective_license_cow: "",
    npp_floor_plans_rm: "",
    npp_floor_plans_rp: "",
    npp_floor_plans_cow: "",
    npp_voa_application_rm: "",
    npp_voa_application_rp: "",
    npp_voa_application_cow: "",
    // Utility Liability fields
    npp_gas_rm: "",
    npp_gas_rp: "",
    npp_gas_csan: "",
    npp_electricity_rm: "",
    npp_electricity_rp: "",
    npp_electricity_csan: "",
    npp_water_rm: "",
    npp_water_rp: "",
    npp_water_csan: "",
    npp_business_rates_rm: "",
    npp_business_rates_rp: "",
    npp_business_rates_csan: "",
    npp_council_tax_rm: "",
    npp_council_tax_rp: "",
    npp_council_tax_csan: "",
    npp_bins_rm: "",
    npp_bins_rp: "",
    npp_bins_csan: "",
    // Health and Safety fields
    npp_gas_safe_rm: "",
    npp_gas_safe_rp: "",
    npp_gas_safe_dr: "",
    npp_gas_safe_cow: "",
    npp_nic_rm: "",
    npp_nic_rp: "",
    npp_nic_dr: "",
    npp_nic_cow: "",
    npp_legionella_rm: "",
    npp_legionella_rp: "",
    npp_legionella_dr: "",
    npp_legionella_cow: "",
    npp_asbestos_rm: "",
    npp_asbestos_rp: "",
    npp_asbestos_dr: "",
    npp_asbestos_cow: "",
    npp_fire_risk_rm: "",
    npp_fire_risk_rp: "",
    npp_fire_risk_dr: "",
    npp_fire_risk_cow: "",
    npp_fire_alarm_rm: "",
    npp_fire_alarm_rp: "",
    npp_fire_alarm_dr: "",
    npp_fire_alarm_cow: "",
    npp_emergency_lighting_rm: "",
    npp_emergency_lighting_rp: "",
    npp_emergency_lighting_dr: "",
    npp_emergency_lighting_cow: "",
    npp_fire_safety_rm: "",
    npp_fire_safety_rp: "",
    npp_fire_safety_dr: "",
    npp_fire_safety_cow: "",
    npp_fire_extinguisher_rm: "",
    npp_fire_extinguisher_rp: "",
    npp_fire_extinguisher_dr: "",
    npp_fire_extinguisher_cow: "",
    // Maintenance Liability fields
    npp_initial_fitout_rm: "",
    npp_initial_fitout_rp: "",
    npp_initial_fitout_cow: "",
    npp_locks_keys_rm: "",
    npp_locks_keys_rp: "",
    npp_locks_keys_cow: "",
    npp_ongoing_internal_maintenance_rm: "",
    npp_ongoing_internal_maintenance_rp: "",
    npp_ongoing_internal_maintenance_cow: "",
    npp_boiler_repair_rm: "",
    npp_boiler_repair_rp: "",
    npp_boiler_repair_cow: "",
    npp_ongoing_external_maintenance_rm: "",
    npp_ongoing_external_maintenance_rp: "",
    npp_ongoing_external_maintenance_cow: "",
    npp_cleaning_areas_rm: "",
    npp_cleaning_areas_rp: "",
    npp_cleaning_areas_cow: "",
    npp_emergency_repairs_rm: "",
    npp_emergency_repairs_rp: "",
    npp_emergency_repairs_cow: "",
    npp_gardening_rm: "",
    npp_gardening_rp: "",
    npp_gardening_cow: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [property, setProperty] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    dropdown_options: ["Please Select", "Owner", "GGM"],
    sex: ["Please Select", "Male", "Female"],
    clientContacts: ["Please Select"],
    clientContactsMap: {}, // Store the mapping of contact names to IDs
    councilTaxLiability: ["Please Select", "Global Guardians", "Metropolitan"],
    fullSecurity: ["Please Select", "Full Security", "Key Holding", "N/A"],
    liabilityOptions: [
      "Please Select",
      "The Owner",
      "GGM",
      "Guardian",
      "N/A",
      "Other",
    ],
    documentationOptions: ["Please Select", "Yes", "No", "N/A"],
  });

  const [isMetropolitanHousing, setIsMetropolitanHousing] = useState(false);
  const [liabilityTypes, setLiabilityTypes] = useState([]);
  const [propertyLiabilities, setPropertyLiabilities] = useState({});

  useEffect(() => {
    loadInitialData();
  }, [propertyId]);

  // No need for date picker initialization with HTML5 date input

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load both property proposal create data and existing proposal data
      const [createDataResponse, proposalDataResponse] = await Promise.allSettled([
        propertyProposalService.getCreateData(propertyId),
        propertyProposalService.getPropertyProposal(propertyId, proposalId)
      ]);
      
      const propertyData = createDataResponse.status === 'fulfilled' ? createDataResponse.value : null;
      const proposalData = proposalDataResponse.status === 'fulfilled' ? proposalDataResponse.value : null;
      
      console.log("Property proposal create data loaded:", propertyData);
      console.log("Property proposal data loaded:", proposalData);
      console.log("Property proposal data loaded - FULL OBJECT:", JSON.stringify(proposalData, null, 2));
      
      if (!propertyData) {
        throw new Error("Failed to load property data");
      }
      
      if (!proposalData) {
        console.warn("Failed to load existing proposal data, using default values");
      }

      setProperty(propertyData.property);

      // Check if it's Metropolitan Housing
      setIsMetropolitanHousing(
        propertyData.property?.isMetropolitanHousing || false
      );

      // Set initial form data from existing proposal data
      console.log("Setting form data with proposalData:", proposalData);
      console.log("account_manager_sex value:", proposalData?.account_manager_sex);
      console.log("Converted account_manager_sex:", proposalData?.account_manager_sex !== undefined 
        ? (proposalData.account_manager_sex === 0 ? "Male" : proposalData.account_manager_sex === 1 ? "Female" : "")
        : "");
      
      setFormData((prev) => ({
        ...prev,
        client: proposalData?.client || propertyData.property?.client?.name || "",
        address: proposalData?.address || propertyData.property?.client?.fullAddress || "",
        property_address_1: proposalData?.property_address_1 ?? propertyData.property?.address_1 ?? "",
        property_address_2: proposalData?.property_address_2 ?? propertyData.property?.address_2 ?? "",
        property_address_3: proposalData?.property_address_3 ?? propertyData.property?.address_3 ?? "",
        property_town: proposalData?.property_town ?? propertyData.property?.town ?? "",
        property_postcode: proposalData?.property_postcode ?? propertyData.property?.postcode ?? "",
        proposal_date: proposalData?.proposal_date
          ? convertToDisplayFormat(proposalData.proposal_date)
          : (propertyData.now
              ? convertToDisplayFormat(propertyData.now)
              : convertToDisplayFormat(new Date().toISOString())),
        account_manager: proposalData?.account_manager || propertyData.property?.client?.manager?.fullName || "",
        account_manager_email: proposalData?.account_manager_email || propertyData.property?.client?.manager?.email || "",
        account_manager_sex: proposalData?.account_manager_sex !== undefined
          ? (proposalData.account_manager_sex === 0 ? "Male" : proposalData.account_manager_sex === 1 ? "Female" : "")
          : "",
        client_contact_id: proposalData?.client_contact_id || "",
        // General fields
        council_tax_liability: proposalData?.council_tax_liability || "",
        full_security: proposalData?.full_security || "",
        proposed_works: parseProposedWorks(proposalData?.proposed_works),
        unusual_matters: proposalData?.unusual_matters || "",
        // MTVH Other section fields
        sia_security_guards: proposalData?.sia_security_guards !== undefined 
          ? String(proposalData.sia_security_guards) 
          : "",
        sia_security_guards_description: proposalData?.sia_security_guards_description 
          || propertyData.otherClauses?.sia_security_guards_description 
          || "",
        caretakers: proposalData?.caretakers !== undefined 
          ? String(proposalData.caretakers) 
          : "",
        caretakers_description: proposalData?.caretakers_description 
          || propertyData.otherClauses?.caretakers_description 
          || "",
        reduce_empty_business_rates: proposalData?.reduce_empty_business_rates !== undefined 
          ? String(proposalData.reduce_empty_business_rates) 
          : "",
        reduce_empty_business_rates_description: proposalData?.reduce_empty_business_rates_description 
          || propertyData.otherClauses?.reduce_empty_business_rates_description 
          || "",
        amount_to_return: proposalData?.amount_to_return || "",
        start_up_fees: proposalData?.start_up_fees 
          || propertyData.otherClauses?.start_up_fees 
          || "",
        min_contract_period: proposalData?.min_contract_period || "",
        min_contract_period_word: proposalData?.min_contract_period_word || "",
        // Non-MTVH fields
        asbestos: proposalData?.asbestos || "",
        asbestos_free_of_charge: proposalData?.asbestos_free_of_charge || "",
        legionella: proposalData?.legionella || "",
        utilities_maintenance: proposalData?.utilities_maintenance || "",
        utilities_gas_electric: proposalData?.utilities_gas_electric || "",
        utilities_water: proposalData?.utilities_water || "",
        council_tax: proposalData?.council_tax || "",
        business_rates: proposalData?.business_rates || "",
        waste_arrange: proposalData?.waste_arrange || "",
        waste_costs: proposalData?.waste_costs || "",
        managing_the_property: proposalData?.managing_the_property || "",
        initial_fit_out_works: proposalData?.initial_fit_out_works || "",
        npp_minimum_contract_period: proposalData?.npp_minimum_contract_period || "",
        npp_notice_period: proposalData?.npp_notice_period || "",
        npp_revenue_share: proposalData?.npp_revenue_share || "",
        npp_public_liability: proposalData?.npp_public_liability || "",
        npp_employers_liability: proposalData?.npp_employers_liability || "",
        npp_professional_idemnity: proposalData?.npp_professional_idemnity || "",
        // Health and Safety fields
        npp_gas_safe_rm: proposalData?.npp_gas_safe_rm || "",
        npp_gas_safe_rp: proposalData?.npp_gas_safe_rp || "",
        npp_gas_safe_dr: proposalData?.npp_gas_safe_dr || "",
        npp_gas_safe_cow: proposalData?.npp_gas_safe_cow || "",
        npp_nic_rm: proposalData?.npp_nic_rm || "",
        npp_nic_rp: proposalData?.npp_nic_rp || "",
        npp_nic_dr: proposalData?.npp_nic_dr || "",
        npp_nic_cow: proposalData?.npp_nic_cow || "",
        npp_legionella_rm: proposalData?.npp_legionella_rm || "",
        npp_legionella_rp: proposalData?.npp_legionella_rp || "",
        npp_legionella_dr: proposalData?.npp_legionella_dr || "",
        npp_legionella_cow: proposalData?.npp_legionella_cow || "",
        npp_asbestos_rm: proposalData?.npp_asbestos_rm || "",
        npp_asbestos_rp: proposalData?.npp_asbestos_rp || "",
        npp_asbestos_dr: proposalData?.npp_asbestos_dr || "",
        npp_asbestos_cow: proposalData?.npp_asbestos_cow || "",
        npp_fire_risk_rm: proposalData?.npp_fire_risk_rm || "",
        npp_fire_risk_rp: proposalData?.npp_fire_risk_rp || "",
        npp_fire_risk_dr: proposalData?.npp_fire_risk_dr || "",
        npp_fire_risk_cow: proposalData?.npp_fire_risk_cow || "",
        npp_fire_alarm_rm: proposalData?.npp_fire_alarm_rm || "",
        npp_fire_alarm_rp: proposalData?.npp_fire_alarm_rp || "",
        npp_fire_alarm_dr: proposalData?.npp_fire_alarm_dr || "",
        npp_fire_alarm_cow: proposalData?.npp_fire_alarm_cow || "",
        npp_emergency_lighting_rm: proposalData?.npp_emergency_lighting_rm || "",
        npp_emergency_lighting_rp: proposalData?.npp_emergency_lighting_rp || "",
        npp_emergency_lighting_dr: proposalData?.npp_emergency_lighting_dr || "",
        npp_emergency_lighting_cow: proposalData?.npp_emergency_lighting_cow || "",
        npp_fire_safety_rm: proposalData?.npp_fire_safety_rm || "",
        npp_fire_safety_rp: proposalData?.npp_fire_safety_rp || "",
        npp_fire_safety_dr: proposalData?.npp_fire_safety_dr || "",
        npp_fire_safety_cow: proposalData?.npp_fire_safety_cow || "",
        npp_fire_extinguisher_rm: proposalData?.npp_fire_extinguisher_rm || "",
        npp_fire_extinguisher_rp: proposalData?.npp_fire_extinguisher_rp || "",
        npp_fire_extinguisher_dr: proposalData?.npp_fire_extinguisher_dr || "",
        npp_fire_extinguisher_cow: proposalData?.npp_fire_extinguisher_cow || "",
        // Liabilities fields
        npp_myglobe_subscription_rm: proposalData?.npp_myglobe_subscription_rm || "",
        npp_myglobe_subscription_rp: proposalData?.npp_myglobe_subscription_rp || "",
        npp_myglobe_subscription_cow: proposalData?.npp_myglobe_subscription_cow || "",
        npp_management_fee_rm: proposalData?.npp_management_fee_rm || "",
        npp_management_fee_rp: proposalData?.npp_management_fee_rp || "",
        npp_management_fee_cow: proposalData?.npp_management_fee_cow || "",
        npp_building_insurance_rm: proposalData?.npp_building_insurance_rm || "",
        npp_building_insurancee_rp: proposalData?.npp_building_insurancee_rp || "",
        npp_building_insurance_cow: proposalData?.npp_building_insurance_cow || "",
        npp_public_liability_insurance_rm: proposalData?.npp_public_liability_insurance_rm || "",
        npp_public_liability_insurance_rp: proposalData?.npp_public_liability_insurance_rp || "",
        npp_public_liability_insurance_cow: proposalData?.npp_public_liability_insurance_cow || "",
        npp_sia_security_rm: proposalData?.npp_sia_security_rm || "",
        npp_sia_security_rp: proposalData?.npp_sia_security_rp || "",
        npp_sia_security_cow: proposalData?.npp_sia_security_cow || "",
        npp_catetaker_rm: proposalData?.npp_catetaker_rm || "",
        npp_catetaker_rp: proposalData?.npp_catetaker_rp || "",
        npp_catetaker_cow: proposalData?.npp_catetaker_cow || "",
        npp_access_egress_rm: proposalData?.npp_access_egress_rm || "",
        npp_access_egress_rp: proposalData?.npp_access_egress_rp || "",
        npp_access_egress_cow: proposalData?.npp_access_egress_cow || "",
        npp_monthly_inspections_rm: proposalData?.npp_monthly_inspections_rm || "",
        npp_monthly_inspections_rp: proposalData?.npp_monthly_inspections_rp || "",
        npp_monthly_inspections_cow: proposalData?.npp_monthly_inspections_cow || "",
        npp_weekly_inspections_rm: proposalData?.npp_weekly_inspections_rm || "",
        npp_weekly_inspections_rp: proposalData?.npp_weekly_inspections_rp || "",
        npp_weekly_inspections_cow: proposalData?.npp_weekly_inspections_cow || "",
        npp_hmo_liscense_rm: proposalData?.npp_hmo_liscense_rm || "",
        npp_hmo_liscense_rp: proposalData?.npp_hmo_liscense_rp || "",
        npp_hmo_liscense_cow: proposalData?.npp_hmo_liscense_cow || "",
        npp_selective_license_rm: proposalData?.npp_selective_license_rm || "",
        npp_selective_license_rp: proposalData?.npp_selective_license_rp || "",
        npp_selective_license_cow: proposalData?.npp_selective_license_cow || "",
        npp_floor_plans_rm: proposalData?.npp_floor_plans_rm || "",
        npp_floor_plans_rp: proposalData?.npp_floor_plans_rp || "",
        npp_floor_plans_cow: proposalData?.npp_floor_plans_cow || "",
        npp_voa_application_rm: proposalData?.npp_voa_application_rm || "",
        npp_voa_application_rp: proposalData?.npp_voa_application_rp || "",
        npp_voa_application_cow: proposalData?.npp_voa_application_cow || "",
        // Utility Liability fields
        npp_gas_rm: proposalData?.npp_gas_rm || "",
        npp_gas_rp: proposalData?.npp_gas_rp || "",
        npp_gas_csan: proposalData?.npp_gas_csan || "",
        npp_electricity_rm: proposalData?.npp_electricity_rm || "",
        npp_electricity_rp: proposalData?.npp_electricity_rp || "",
        npp_electricity_csan: proposalData?.npp_electricity_csan || "",
        npp_water_rm: proposalData?.npp_water_rm || "",
        npp_water_rp: proposalData?.npp_water_rp || "",
        npp_water_csan: proposalData?.npp_water_csan || "",
        npp_business_rates_rm: proposalData?.npp_business_rates_rm || "",
        npp_business_rates_rp: proposalData?.npp_business_rates_rp || "",
        npp_business_rates_csan: proposalData?.npp_business_rates_csan || "",
        npp_council_tax_rm: proposalData?.npp_council_tax_rm || "",
        npp_council_tax_rp: proposalData?.npp_council_tax_rp || "",
        npp_council_tax_csan: proposalData?.npp_council_tax_csan || "",
        npp_bins_rm: proposalData?.npp_bins_rm || "",
        npp_bins_rp: proposalData?.npp_bins_rp || "",
        npp_bins_csan: proposalData?.npp_bins_csan || "",
        // Maintenance Liability fields
        npp_initial_fitout_rm: proposalData?.npp_initial_fitout_rm || "",
        npp_initial_fitout_rp: proposalData?.npp_initial_fitout_rp || "",
        npp_initial_fitout_cow: proposalData?.npp_initial_fitout_cow || "",
        npp_locks_keys_rm: proposalData?.npp_locks_keys_rm || "",
        npp_locks_keys_rp: proposalData?.npp_locks_keys_rp || "",
        npp_locks_keys_cow: proposalData?.npp_locks_keys_cow || "",
        npp_ongoing_internal_maintenance_rm: proposalData?.npp_ongoing_internal_maintenance_rm || "",
        npp_ongoing_internal_maintenance_rp: proposalData?.npp_ongoing_internal_maintenance_rp || "",
        npp_ongoing_internal_maintenance_cow: proposalData?.npp_ongoing_internal_maintenance_cow || "",
        npp_boiler_repair_rm: proposalData?.npp_boiler_repair_rm || "",
        npp_boiler_repair_rp: proposalData?.npp_boiler_repair_rp || "",
        npp_boiler_repair_cow: proposalData?.npp_boiler_repair_cow || "",
        npp_ongoing_external_maintenance_rm: proposalData?.npp_ongoing_external_maintenance_rm || "",
        npp_ongoing_external_maintenance_rp: proposalData?.npp_ongoing_external_maintenance_rp || "",
        npp_ongoing_external_maintenance_cow: proposalData?.npp_ongoing_external_maintenance_cow || "",
        npp_cleaning_areas_rm: proposalData?.npp_cleaning_areas_rm || "",
        npp_cleaning_areas_rp: proposalData?.npp_cleaning_areas_rp || "",
        npp_cleaning_areas_cow: proposalData?.npp_cleaning_areas_cow || "",
        npp_emergency_repairs_rm: proposalData?.npp_emergency_repairs_rm || "",
        npp_emergency_repairs_rp: proposalData?.npp_emergency_repairs_rp || "",
        npp_emergency_repairs_cow: proposalData?.npp_emergency_repairs_cow || "",
        npp_gardening_rm: proposalData?.npp_gardening_rm || "",
        npp_gardening_rp: proposalData?.npp_gardening_rp || "",
        npp_gardening_cow: proposalData?.npp_gardening_cow || "",
      }));

      // Load client contacts from API response
      if (
        propertyData.clientContacts &&
        Object.keys(propertyData.clientContacts).length > 0
      ) {
        const contacts = [
          "Please Select",
          ...Object.values(propertyData.clientContacts),
        ];
        setDropdownData((prev) => ({
          ...prev,
          clientContacts: contacts,
          clientContactsMap: propertyData.clientContacts,
        }));
      } else if (propertyData.property?.client?.contacts) {
        // Fallback to property client contacts
        const contacts = [
          "Please Select",
          ...propertyData.property.client.contacts.map((c) => c.fullName),
        ];
        // Create a mapping for fallback case
        const contactsMap = {};
        propertyData.property.client.contacts.forEach((c, index) => {
          contactsMap[c.id] = c.fullName;
        });
        setDropdownData((prev) => ({
          ...prev,
          clientContacts: contacts,
          clientContactsMap: contactsMap,
        }));
      }

      // Load dropdown options from API response
      if (propertyData.dropdownOptions) {
        setDropdownData((prev) => ({
          ...prev,
          dropdown_options: ["Please Select", ...propertyData.dropdownOptions],
        }));
      }

      if (propertyData.sexOptions) {
        setDropdownData((prev) => ({
          ...prev,
          sex: [
            "Please Select",
            ...Object.values(propertyData.sexOptions).filter(
              (v) => v !== "Please Select"
            ),
          ],
        }));
      }

      if (propertyData.councilTaxLiability) {
        setDropdownData((prev) => ({
          ...prev,
          councilTaxLiability: [
            "Please Select",
            ...propertyData.councilTaxLiability,
          ],
        }));
      }

      if (propertyData.fullSecurity) {
        setDropdownData((prev) => ({
          ...prev,
          fullSecurity: ["Please Select", ...propertyData.fullSecurity],
        }));
      }

      if (propertyData.liabilityOptions) {
        // liabilityOptions is an object with id as key and name as value (from Laravel pluck)
        // Convert to array for dropdown, but keep the map for value lookup
        try {
          // Check if it's already an array
          if (Array.isArray(propertyData.liabilityOptions)) {
            // Filter out "Please Select" if it exists
            const filtered = propertyData.liabilityOptions.filter(
              (opt) => opt !== "Please Select"
            );
            setDropdownData((prev) => ({
              ...prev,
              liabilityOptions: ["Please Select", ...filtered],
            }));
          } else if (typeof propertyData.liabilityOptions === "object") {
            // It's an object - convert to array
            const optionsArray = Object.entries(propertyData.liabilityOptions).map(
              ([id, name]) => ({ id: parseInt(id), name })
            );
            setDropdownData((prev) => ({
              ...prev,
              liabilityOptions: ["Please Select", ...optionsArray.map((o) => o.name)],
              liabilityOptionsMap: propertyData.liabilityOptions, // Keep the map for form submission
            }));
          }
        } catch (error) {
          console.error("Error processing liabilityOptions:", error);
          // Fallback to default options
          setDropdownData((prev) => ({
            ...prev,
            liabilityOptions: [
              "Please Select",
              "The Owner",
              "GGM",
              "Guardian",
              "N/A",
              "Other",
            ],
          }));
        }
      }

      if (propertyData.documentationOptions) {
        setDropdownData((prev) => ({
          ...prev,
          documentationOptions: [
            "Please Select",
            ...propertyData.documentationOptions,
          ],
        }));
      }

      // Load liability types for MTVH properties
      if (propertyData.liabilityTypes) {
        console.log(
          `✅ [PROPERTY PROPOSAL EDIT] Loaded ${propertyData.liabilityTypes.length} liability types`
        );
        setLiabilityTypes(propertyData.liabilityTypes);
      } else {
        console.log(
          "⚠️ [PROPERTY PROPOSAL EDIT] No liabilityTypes in propertyData"
        );
      }

      // Load existing property liabilities (for edit)
      if (propertyData.propertyLiabilities) {
        console.log(
          `✅ [PROPERTY PROPOSAL EDIT] Loaded ${Object.keys(propertyData.propertyLiabilities).length} existing property liabilities`
        );
        setPropertyLiabilities(propertyData.propertyLiabilities);
        
        // Populate formData with existing liability values
        const liabilityFormData = {};
        Object.keys(propertyData.propertyLiabilities).forEach((typeId) => {
          const liability = propertyData.propertyLiabilities[typeId];
          // Convert to string for form input (dropdowns expect string values)
          liabilityFormData[`liability[${typeId}][work]`] = liability.work ? String(liability.work) : "";
          liabilityFormData[`liability[${typeId}][cost]`] = liability.cost ? String(liability.cost) : "";
          console.log(
            `🔍 [PROPERTY PROPOSAL EDIT] Liability type ${typeId}: work=${liability.work}, cost=${liability.cost}`
          );
        });
        
        console.log(
          "✅ [PROPERTY PROPOSAL EDIT] Populating formData with liability values:",
          liabilityFormData
        );
        
        // Update formData with liability values
        setFormData((prev) => ({
          ...prev,
          ...liabilityFormData,
        }));
      } else {
        console.log(
          "⚠️ [PROPERTY PROPOSAL EDIT] No propertyLiabilities in propertyData"
        );
      }

      // Debug: Log MTVH status and liabilityTypes
      console.log(
        `🔍 [PROPERTY PROPOSAL EDIT] isMetropolitanHousing: ${propertyData.property?.isMetropolitanHousing}, liabilityTypes count: ${propertyData.liabilityTypes?.length || 0}`
      );

      // No longer using mock data
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleProposedWorkChange = (index, value) => {
    const newProposedWorks = [...formData.proposed_works];
    newProposedWorks[index] = value;
    setFormData((prev) => ({
      ...prev,
      proposed_works: newProposedWorks,
    }));
  };

  const addProposedWork = () => {
    setFormData((prev) => ({
      ...prev,
      proposed_works: [...prev.proposed_works, ""],
    }));
  };

  const removeProposedWork = (index) => {
    const newProposedWorks = formData.proposed_works.filter(
      (_, i) => i !== index
    );
    setFormData((prev) => ({
      ...prev,
      proposed_works: newProposedWorks,
    }));
  };

  const validateForm = () => {
    const errors = {};

    // Required fields validation (matching Laravel CreatePropertyProposalRequest)
    if (!formData.status_id && formData.status_id !== 0)
      errors.status_id = "Status is required";
    if (!formData.client || formData.client.trim() === "")
      errors.client = "Client is required";
    if (!formData.client_contact_id)
      errors.client_contact_id = "Client Contact is required";
    if (!formData.address || formData.address.trim() === "")
      errors.address = "Client Address is required";
    if (
      !formData.property_address_1 ||
      formData.property_address_1.trim() === ""
    )
      errors.property_address_1 = "Property Address 1 is required";
    if (!formData.proposal_date)
      errors.proposal_date = "Proposal Date is required";
    if (!formData.account_manager || formData.account_manager.trim() === "")
      errors.account_manager = "Account Manager is required";
    if (
      !formData.account_manager_email ||
      formData.account_manager_email.trim() === ""
    )
      errors.account_manager_email = "Account Manager Email is required";

    // Validate proposal date format (DD/MM/YYYY)
    if (formData.proposal_date) {
      const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      if (!dateRegex.test(formData.proposal_date)) {
        errors.proposal_date = "Proposal Date must be in DD/MM/YYYY format";
      }
    }

    if (isMetropolitanHousing) {
      if (!formData.initial_fit_out_works)
        errors.initial_fit_out_works = "Initial fit out works is required";
      if (!formData.sia_security_guards && formData.sia_security_guards !== 0)
        errors.sia_security_guards = "SIA Security Guards is required";
      if (!formData.caretakers && formData.caretakers !== 0)
        errors.caretakers = "Caretakers is required";
      if (
        !formData.reduce_empty_business_rates &&
        formData.reduce_empty_business_rates !== 0
      )
        errors.reduce_empty_business_rates =
          "Reduce Empty Business Rates is required";
      if (!formData.min_contract_period)
        errors.min_contract_period = "Min Contract Period is required";
      if (!formData.min_contract_period_word)
        errors.min_contract_period_word =
          "Min Contract Period Word is required";
      if (!formData.business_rates && formData.business_rates !== 0)
        errors.business_rates = "Business Rates is required";
      if (!formData.asbestos) errors.asbestos = "Asbestos is required";
      if (!formData.asbestos_free_of_charge)
        errors.asbestos_free_of_charge = "Asbestos Free of Charge is required";
      if (!formData.legionella) errors.legionella = "Legionella is required";
      if (!formData.utilities_maintenance)
        errors.utilities_maintenance = "Utilities Maintenance is required";
      if (!formData.utilities_gas_electric)
        errors.utilities_gas_electric = "Utilities Gas Electric is required";
      if (!formData.utilities_water)
        errors.utilities_water = "Utilities Water is required";
      if (!formData.council_tax) errors.council_tax = "Council Tax is required";
      if (!formData.waste_arrange)
        errors.waste_arrange = "Waste Arrange is required";
      if (!formData.waste_costs) errors.waste_costs = "Waste Costs is required";
      if (!formData.managing_the_property)
        errors.managing_the_property = "Managing the Property is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setLoading(true);

      // Extract liability fields from formData and format for backend
      const liabilityData = {};
      Object.keys(formData).forEach((key) => {
        const liabilityMatch = key.match(/^liability\[(\d+)\]\[(work|cost)\]$/);
        if (liabilityMatch) {
          const typeId = liabilityMatch[1];
          const field = liabilityMatch[2];
          if (!liabilityData[typeId]) {
            liabilityData[typeId] = {};
          }
          const value = formData[key];
          // Only include if not empty and not "Please Select"
          if (value && value !== "" && value !== "Please Select") {
            // Convert to integer for backend (repository expects numbers)
            liabilityData[typeId][field] = parseInt(value, 10) || null;
          }
        }
      });

      // Filter out entries where both work and cost are empty (matching Laravel behavior)
      Object.keys(liabilityData).forEach((typeId) => {
        const liability = liabilityData[typeId];
        if ((!liability.work || liability.work === "" || liability.work === "Please Select") &&
            (!liability.cost || liability.cost === "" || liability.cost === "Please Select")) {
          delete liabilityData[typeId];
        }
      });

      console.log(
        "🔍 [PROPERTY PROPOSAL EDIT] Extracted liability data:",
        JSON.stringify(liabilityData, null, 2)
      );

      // Prepare data for submission
      const submitData = {
        ...formData,
        // proposal_date is already in DD/MM/YYYY format
        // Convert account_manager_sex from string to number
        account_manager_sex: formData.account_manager_sex === "Male" ? 0 : formData.account_manager_sex === "Female" ? 1 : "",
        proposed_works: JSON.stringify(
          formData.proposed_works.filter((work) => work.trim() !== "")
        ),
        // Add liability data if any exists
        ...(Object.keys(liabilityData).length > 0 && { liability: liabilityData }),
      };

      // Debug logging for property_address_3
      console.log("🔍 [PROPERTY PROPOSAL EDIT] Submitting property_address_3:", {
        formDataValue: formData.property_address_3,
        submitDataValue: submitData.property_address_3,
      });

      await propertyProposalService.updatePropertyProposal(propertyId, proposalId, submitData);
      toast.success("Property Proposal updated successfully!");

      navigate(`/properties/${propertyId}`);
    } catch (error) {
      console.error("Error updating property proposal:", error);

      if (error.response?.data?.errors) {
        // Handle validation errors from backend
        setFormErrors(error.response.data.errors);
        toast.error("Please fix the validation errors");
        return;
      }

      const errorMessage = error.response?.data?.error || error.response?.data?.message || "Failed to update property proposal";
      const errorDetails = error.response?.data?.details || error.response?.data?.driverError || "";
      console.error("Property Proposal Update Error:", {
        message: errorMessage,
        details: errorDetails,
        fullError: error.response?.data
      });
      toast.error(`${errorMessage}${errorDetails ? `: ${errorDetails}` : ""}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !property) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return (
    <div className="property-proposal-create">
      {/* Breadcrumb */}
      {/* <div className="breadcrumb">
        <a href="/properties">Properties</a>
        <span className="separator">/</span>
        <a href={`/properties/${propertyId}`}>{property?.name || 'Property'}</a>
        <span className="separator">/</span>
        <span>Edit Property Proposal</span>
      </div> */}

      <div>
        <div className="panel-form" id="property_proposal">
          <div
            className="panel-heading panel-title"
            style={{
              backgroundColor: "#F8F9FA",
              border: "none",
              fontSize: "29px",
            }}
          >
            <h3
              style={{
                fontSize: "29px",
              }}
            >
              Edit Property Proposal
            </h3>
          </div>
          <div className="panel-body">
            <form onSubmit={handleSubmit} className="form-horizontal" method="post">
              <input type="hidden" name="property_id" value={propertyId} />

              {/* Report Section */}
              <div
                className="report-section-header"
                style={{ fontSize: "29px", fontWeight: "600", border: "none" }}
              >
                Report
              </div>

              <div className="row">
                <div className="col-xs-8">
                  <div className="form-group">
                    <div className="col-xs-12 mb-10">
                      {/* Report Download Button */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#007bff",
                          color: "white",
                          padding: "10px 15px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          width: "fit-content",
                          marginBottom: "15px",
                        }}
                        onClick={async () => {
                          try {
                            // Generate and download the report
                            const reportUrl = `/api/propertyproposals/${proposalId}/download`;
                            
                            // Use fetch to get the file and then create a blob for download
                            const response = await fetch(reportUrl, {
                              method: 'GET',
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                              },
                            });
                            
                            if (!response.ok) {
                              throw new Error(`HTTP error! status: ${response.status}`);
                            }
                            
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            
                            // Create a temporary link element to trigger download
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `property-proposal-${proposalId}-report.pdf`;
                            
                            // Append to body, click, and remove
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            
                            // Clean up the object URL
                            window.URL.revokeObjectURL(url);
                            
                            toast.success('Report downloaded successfully!');
                          } catch (error) {
                            console.error('Error downloading report:', error);
                            toast.error('Failed to download report: ' + error.message);
                          }
                        }}
                      >
                        <i 
                          className="fa fa-file-pdf-o" 
                          style={{ 
                            marginRight: "10px", 
                            fontSize: "18px" 
                          }}
                        ></i>
                        <span style={{ fontWeight: "bold" }}>Report</span>
                        <span style={{ 
                          marginLeft: "10px", 
                          fontSize: "12px", 
                          opacity: "0.9" 
                        }}>
                          Updated: {new Date().toLocaleDateString('en-GB')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xs-4">
                  <div className="form-group">
                    <div className="col-xs-12 mb-10 label-required">
                      <strong>Status</strong>
                    </div>
                    <div className="col-xs-12">
                      <select
                        name="status_id"
                        value={formData.status_id}
                        onChange={handleInputChange}
                        className={`form-control ${
                          formErrors.status_id ? "error" : ""
                        }`}
                        required
                      >
                        <option value="">Please Select</option>
                        <option value={0}>Unpublished</option>
                        <option value={1}>Published</option>
                      </select>
                      {formErrors.status_id && (
                        <span className="error-message">
                          {formErrors.status_id}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* General Section */}
              <div
                className="report-section-header"
                style={{ fontSize: "29px", fontWeight: "600", border: "none" }}
              >
                General
              </div>

              <div className="row">
                <div className="col-xs-8">
                  <div className="form-group">
                    <div className="col-xs-12 mb-10 label-required">
                      <strong>Client</strong>
                    </div>
                    <div className="col-xs-12">
                      <input
                        style={{ width: "700px", marginTop: "-10px" }}
                        type="text"
                        name="client"
                        value={formData.client}
                        onChange={handleInputChange}
                        className={`form-control ${
                          formErrors.client ? "error" : ""
                        }`}
                        required
                      />
                      {formErrors.client && (
                        <span className="error-message">
                          {formErrors.client}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-xs-4">
                  <div className="form-group">
                    <div className="col-xs-12 mb-10 label-required">
                      <strong>Client Contact</strong>
                    </div>
                    <div className="col-sm-12">
                      <select
                        style={{ marginTop: "-10px" }}
                        name="client_contact_id"
                        value={formData.client_contact_id}
                        onChange={handleInputChange}
                        className={`form-control ${
                          formErrors.client_contact_id ? "error" : ""
                        }`}
                        required
                      >
                        {dropdownData.clientContacts.map((contact, index) => {
                          if (index === 0) {
                            return (
                              <option key={index} value="">
                                {contact}
                              </option>
                            );
                          }
                          // Get the contact ID from the clientContactsMap
                          const contactId = Object.keys(
                            dropdownData.clientContactsMap
                          ).find(
                            (key) =>
                              dropdownData.clientContactsMap[key] === contact
                          );
                          return (
                            <option key={index} value={contactId}>
                              {contact}
                            </option>
                          );
                        })}
                      </select>
                      {formErrors.client_contact_id && (
                        <span className="error-message">
                          {formErrors.client_contact_id}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-xs-8">
                  <div className="form-group">
                    <div className="col-xs-12 mb-10 label-required">
                      <strong>Client Address</strong>
                    </div>
                    <div className="col-sm-12">
                      <input
                        style={{
                          width: "700px",
                          marginLeft: "10px",
                          marginTop: "-10px",
                        }}
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className={`form-control ${
                          formErrors.address ? "error" : ""
                        }`}
                        required
                      />
                      {formErrors.address && (
                        <span className="error-message">
                          {formErrors.address}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-xs-4">
                  <div className="form-group">
                    <div className="col-xs-12 mb-10 label-required">
                      <strong>Proposal Date</strong>
                    </div>
                    <div className="col-sm-12" style={{ marginTop: "-10px" }}>
                      <DateInput
                        name="proposal_date"
                        value={formData.proposal_date}
                        onChange={handleInputChange}
                        placeholder="dd/mm/yyyy"
                        className={`form-control ${
                          formErrors.proposal_date ? "error" : ""
                        }`}
                        error={formErrors.proposal_date}
                        required
                      />
                    </div>
                  </div>
                </div>

                {isMetropolitanHousing && (
                  <>
                    <div className="col-xs-6">
                      <div className="form-group">
                        <div className="col-xs-12 mb-10">
                          <strong>Council Tax Liability</strong>
                        </div>
                        <div className="col-sm-12">
                          <select
                            style={{ marginTop: "-10px" }}
                            name="council_tax_liability"
                            value={formData.council_tax_liability}
                            onChange={handleInputChange}
                            className="form-control"
                          >
                            {dropdownData.councilTaxLiability.map(
                              (option, index) => (
                                <option
                                  key={index}
                                  value={index === 0 ? "" : index}
                                >
                                  {option}
                                </option>
                              )
                            )}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12">
                      <div className="form-group">
                        <div className="col-xs-12 mb-10">
                          <strong>Proposed Works</strong>
                        </div>
                        <div className="col-sm-10">
                          <input
                            style={{ marginTop: "-10px" }}
                            className="form-control"
                            type="text"
                            value={formData.proposed_works[0]}
                            onChange={(e) =>
                              handleProposedWorkChange(0, e.target.value)
                            }
                          />
                        </div>
                        <div className="col-sm-2">
                          <button
                            type="button"
                            className="btn-add"
                            onClick={addProposedWork}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                      {formData.proposed_works.slice(1).map((work, index) => (
                        <div key={index + 1} className="proposed-work-item">
                          <input
                            style={{ marginTop: "-10px" }}
                            className="form-control"
                            type="text"
                            value={work}
                            onChange={(e) =>
                              handleProposedWorkChange(
                                index + 1,
                                e.target.value
                              )
                            }
                          />
                          <button
                            type="button"
                            className="remove-work-btn"
                            onClick={() => removeProposedWork(index + 1)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="col-xs-12">
                      <div className="form-group">
                        <div className="col-xs-12 mb-10">
                          <strong>Unusual Matters</strong>
                        </div>
                        <div className="col-sm-12">
                          <textarea
                            name="unusual_matters"
                            value={formData.unusual_matters}
                            onChange={handleInputChange}
                            className="form-control"
                            rows={4}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Property Section */}
              <div
                className="report-section-header"
                style={{ fontSize: "29px", fontWeight: "600", border: "none" }}
              >
                Property
              </div>

              <div className="row">
                <div className="col-xs-12">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "30px",
                      marginBottom: "15px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        width: "280px",
                      }}
                    >
                      <label
                        style={{
                          margin: 0,
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                          width: "80px",
                        }}
                      >
                        Address 1 <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="property_address_1"
                        value={formData.property_address_1}
                        onChange={handleInputChange}
                        className={`form-control ${
                          formErrors.property_address_1 ? "error" : ""
                        }`}
                        style={{ width: "200px" }}
                        required
                      />
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        width: "280px",
                      }}
                    >
                      <label
                        style={{
                          margin: 0,
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                          width: "80px",
                        }}
                      >
                        Address 2
                      </label>
                      <input
                        type="text"
                        name="property_address_2"
                        value={formData.property_address_2}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ width: "200px" }}
                      />
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <label
                        style={{
                          margin: 0,
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                        }}
                      >
                        Address 3
                      </label>
                      <input
                        type="text"
                        name="property_address_3"
                        value={formData.property_address_3}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ width: "250px" }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-xs-12">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "30px",
                      marginBottom: "15px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        width: "280px",
                      }}
                    >
                      <label
                        style={{
                          margin: 0,
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                          width: "80px",
                        }}
                      >
                        Town
                      </label>
                      <input
                        type="text"
                        name="property_town"
                        value={formData.property_town}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ width: "200px" }}
                      />
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        width: "280px",
                      }}
                    >
                      <label
                        style={{
                          margin: 0,
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                          width: "80px",
                        }}
                      >
                        Postcode
                      </label>
                      <input
                        type="text"
                        name="property_postcode"
                        value={formData.property_postcode}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ width: "200px" }}
                      />
                    </div>

                    {formErrors.property_address_1 && (
                      <span className="error-message">
                        {formErrors.property_address_1}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Account Manager Section */}
              <div
                className="report-section-header"
                style={{ fontSize: "29px", fontWeight: "600", border: "none" }}
              >
                Account Manager
              </div>

              <div className="row">
                <div className="col-xs-12">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "30px",
                      marginBottom: "15px",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <label
                        style={{
                          margin: 0,
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                        }}
                      >
                        Name <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="account_manager"
                        value={formData.account_manager}
                        onChange={handleInputChange}
                        className={`form-control ${
                          formErrors.account_manager ? "error" : ""
                        }`}
                        style={{ width: "200px" }}
                        required
                      />
                      {formErrors.account_manager && (
                        <span className="error-message">
                          {formErrors.account_manager}
                        </span>
                      )}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <label
                        style={{
                          margin: 0,
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                        }}
                      >
                        Email <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type="email"
                        name="account_manager_email"
                        value={formData.account_manager_email}
                        onChange={handleInputChange}
                        className={`form-control ${
                          formErrors.account_manager_email ? "error" : ""
                        }`}
                        style={{ width: "250px" }}
                        required
                      />
                      {formErrors.account_manager_email && (
                        <span className="error-message">
                          {formErrors.account_manager_email}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {isMetropolitanHousing && (
                <>
                  <div className="row">
                    <div className="col-xs-12">
                      <div className="form-group">
                        <label className="control-label col-sm-4 label-required">
                          Initial fit out and clearance list work
                        </label>
                        <div className="col-sm-8">
                          <textarea
                            name="initial_fit_out_works"
                            value={formData.initial_fit_out_works}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.initial_fit_out_works ? "error" : ""
                            }`}
                            rows={4}
                            required
                          />
                          {formErrors.initial_fit_out_works && (
                            <span className="error-message">
                              {formErrors.initial_fit_out_works}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className="report-section-header"
                    style={{
                      fontSize: "29px",
                      fontWeight: "600",
                      border: "none",
                    }}
                  >
                    Other
                  </div>

                  {/* SIA Security Guards */}
                  <div className="row">
                    <div className="col-xs-5">
                      <div className="form-group">
                        <label className="control-label col-sm-8 label-required">
                          SIA Security Guards
                        </label>
                        <div className="col-sm-4">
                          <select
                            name="sia_security_guards"
                            value={formData.sia_security_guards}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.sia_security_guards ? "error" : ""
                            }`}
                            required
                          >
                            <option value="">Please Select</option>
                            <option value={0}>No</option>
                            <option value={1}>Yes</option>
                          </select>
                          {formErrors.sia_security_guards && (
                            <span className="error-message">
                              {formErrors.sia_security_guards}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Description - Only show when "Yes" (1) is selected - matching Laravel js-show-hide logic */}
                    {(formData.sia_security_guards === "1" ||
                      formData.sia_security_guards === 1 ||
                      parseInt(formData.sia_security_guards) === 1) ? (
                      <div className="col-xs-7">
                        <div className="form-group">
                          <label className="control-label col-sm-2">
                            Description
                          </label>
                          <div className="col-sm-10">
                            <textarea
                              name="sia_security_guards_description"
                              value={formData.sia_security_guards_description}
                              onChange={handleInputChange}
                              className="form-control"
                              rows={8}
                            />
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {/* Caretakers */}
                  <div className="row">
                    <div className="col-xs-5">
                      <div className="form-group">
                        <label className="control-label col-sm-8 label-required">
                          Caretakers
                        </label>
                        <div className="col-sm-4">
                          <select
                            name="caretakers"
                            value={formData.caretakers}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.caretakers ? "error" : ""
                            }`}
                            required
                          >
                            <option value="">Please Select</option>
                            <option value={0}>No</option>
                            <option value={1}>Yes</option>
                          </select>
                          {formErrors.caretakers && (
                            <span className="error-message">
                              {formErrors.caretakers}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Description - Only show when "Yes" (1) is selected - matching Laravel js-show-hide logic */}
                    {(formData.caretakers === "1" ||
                      formData.caretakers === 1 ||
                      parseInt(formData.caretakers) === 1) ? (
                      <div className="col-xs-7">
                        <div className="form-group">
                          <label className="control-label col-sm-2">
                            Description
                          </label>
                          <div className="col-sm-10">
                            <textarea
                              name="caretakers_description"
                              value={formData.caretakers_description}
                              onChange={handleInputChange}
                              className="form-control"
                              rows={8}
                            />
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {/* Reduce Empty Business Rates */}
                  <div className="row">
                    <div className="col-xs-5">
                      <div className="form-group">
                        <label className="control-label col-sm-8 label-required">
                          Reduce Empty Business Rates
                        </label>
                        <div className="col-sm-4">
                          <select
                            name="reduce_empty_business_rates"
                            value={formData.reduce_empty_business_rates}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.reduce_empty_business_rates
                                ? "error"
                                : ""
                            }`}
                            required
                          >
                            <option value="">Please Select</option>
                            <option value={0}>No</option>
                            <option value={1}>Yes</option>
                          </select>
                          {formErrors.reduce_empty_business_rates && (
                            <span className="error-message">
                              {formErrors.reduce_empty_business_rates}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Description - Only show when "Yes" (1) is selected - matching Laravel js-show-hide logic */}
                    {(formData.reduce_empty_business_rates === "1" ||
                      formData.reduce_empty_business_rates === 1 ||
                      parseInt(formData.reduce_empty_business_rates) === 1) ? (
                      <div className="col-xs-7">
                        <div className="form-group">
                          <label className="control-label col-sm-2">
                            Description
                          </label>
                          <div className="col-sm-10">
                            <textarea
                              name="reduce_empty_business_rates_description"
                              value={
                                formData.reduce_empty_business_rates_description
                              }
                              onChange={handleInputChange}
                              className="form-control"
                              rows={15}
                            />
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {/* Amount to Return and Start Up Fees */}
                  <div className="row">
                    <div className="col-xs-5">
                      <div className="form-group">
                        <label className="control-label col-sm-6">
                          Amount to Return
                        </label>
                        <div className="col-sm-6">
                          <input
                            type="text"
                            name="amount_to_return"
                            value={formData.amount_to_return}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-7">
                      <div className="form-group">
                        <label className="control-label col-sm-3">
                          Start up fees
                        </label>
                        <div className="col-sm-9">
                          <textarea
                            name="start_up_fees"
                            value={formData.start_up_fees}
                            onChange={handleInputChange}
                            className="form-control"
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Min Contract Period */}
                  <div className="row">
                    <div className="col-xs-5">
                      <div className="form-group">
                        <label className="control-label col-sm-8 label-required">
                          Min Contractor Period (months)
                        </label>
                        <div className="col-sm-4">
                          <input
                            type="number"
                            name="min_contract_period"
                            value={formData.min_contract_period}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.min_contract_period ? "error" : ""
                            }`}
                            required
                          />
                          {formErrors.min_contract_period && (
                            <span className="error-message">
                              {formErrors.min_contract_period}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-7">
                      <div className="form-group">
                        <label className="control-label col-sm-6 label-required">
                          Min CP in words (months)
                        </label>
                        <div className="col-sm-6">
                          <input
                            type="text"
                            name="min_contract_period_word"
                            value={formData.min_contract_period_word}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.min_contract_period_word ? "error" : ""
                            }`}
                            required
                          />
                          {formErrors.min_contract_period_word && (
                            <span className="error-message">
                              {formErrors.min_contract_period_word}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Utility and Liability Fields */}
                  <div className="row">
                    <div className="col-xs-4">
                      <div className="form-group">
                        <label className="control-label col-sm-7 label-required">
                          Business Rates
                        </label>
                        <div className="col-sm-5">
                          <select
                            name="business_rates"
                            value={formData.business_rates}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.business_rates ? "error" : ""
                            }`}
                            required
                          >
                            <option value="">Please Select</option>
                            <option value={0}>No</option>
                            <option value={1}>Yes</option>
                          </select>
                          {formErrors.business_rates && (
                            <span className="error-message">
                              {formErrors.business_rates}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="form-group">
                        <label className="control-label col-sm-7 label-required">
                          Asbestos
                        </label>
                        <div className="col-sm-5">
                          <select
                            name="asbestos"
                            value={formData.asbestos}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.asbestos ? "error" : ""
                            }`}
                            required
                          >
                            {dropdownData.dropdown_options.map(
                              (option, index) => (
                                <option
                                  key={index}
                                  value={index === 0 ? "" : option}
                                >
                                  {option}
                                </option>
                              )
                            )}
                          </select>
                          {formErrors.asbestos && (
                            <span className="error-message">
                              {formErrors.asbestos}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="form-group">
                        <label className="control-label col-sm-7 label-required">
                          Asbestos Free of Charge
                        </label>
                        <div className="col-sm-5">
                          <select
                            name="asbestos_free_of_charge"
                            value={formData.asbestos_free_of_charge}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.asbestos_free_of_charge ? "error" : ""
                            }`}
                            required
                          >
                            {dropdownData.dropdown_options.map(
                              (option, index) => (
                                <option
                                  key={index}
                                  value={index === 0 ? "" : option}
                                >
                                  {option}
                                </option>
                              )
                            )}
                          </select>
                          {formErrors.asbestos_free_of_charge && (
                            <span className="error-message">
                              {formErrors.asbestos_free_of_charge}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="form-group">
                        <label className="control-label col-sm-7 label-required">
                          Legionella
                        </label>
                        <div className="col-sm-5">
                          <select
                            name="legionella"
                            value={formData.legionella}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.legionella ? "error" : ""
                            }`}
                            required
                          >
                            {dropdownData.dropdown_options.map(
                              (option, index) => (
                                <option
                                  key={index}
                                  value={index === 0 ? "" : option}
                                >
                                  {option}
                                </option>
                              )
                            )}
                          </select>
                          {formErrors.legionella && (
                            <span className="error-message">
                              {formErrors.legionella}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="form-group">
                        <label className="control-label col-sm-7 label-required">
                          Utilities Maintenance
                        </label>
                        <div className="col-sm-5">
                          <select
                            name="utilities_maintenance"
                            value={formData.utilities_maintenance}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.utilities_maintenance ? "error" : ""
                            }`}
                            required
                          >
                            {dropdownData.dropdown_options.map(
                              (option, index) => (
                                <option
                                  key={index}
                                  value={index === 0 ? "" : option}
                                >
                                  {option}
                                </option>
                              )
                            )}
                          </select>
                          {formErrors.utilities_maintenance && (
                            <span className="error-message">
                              {formErrors.utilities_maintenance}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="form-group">
                        <label className="control-label col-sm-7 label-required">
                          Utilities Gas Electric
                        </label>
                        <div className="col-sm-5">
                          <select
                            name="utilities_gas_electric"
                            value={formData.utilities_gas_electric}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.utilities_gas_electric ? "error" : ""
                            }`}
                            required
                          >
                            {dropdownData.dropdown_options.map(
                              (option, index) => (
                                <option
                                  key={index}
                                  value={index === 0 ? "" : option}
                                >
                                  {option}
                                </option>
                              )
                            )}
                          </select>
                          {formErrors.utilities_gas_electric && (
                            <span className="error-message">
                              {formErrors.utilities_gas_electric}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="form-group">
                        <label className="control-label col-sm-7 label-required">
                          Utilities Water
                        </label>
                        <div className="col-sm-5">
                          <select
                            name="utilities_water"
                            value={formData.utilities_water}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.utilities_water ? "error" : ""
                            }`}
                            required
                          >
                            {dropdownData.dropdown_options.map(
                              (option, index) => (
                                <option
                                  key={index}
                                  value={index === 0 ? "" : option}
                                >
                                  {option}
                                </option>
                              )
                            )}
                          </select>
                          {formErrors.utilities_water && (
                            <span className="error-message">
                              {formErrors.utilities_water}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="form-group">
                        <label className="control-label col-sm-7 label-required">
                          Council Tax
                        </label>
                        <div className="col-sm-5">
                          <select
                            name="council_tax"
                            value={formData.council_tax}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.council_tax ? "error" : ""
                            }`}
                            required
                          >
                            {dropdownData.dropdown_options.map(
                              (option, index) => (
                                <option
                                  key={index}
                                  value={index === 0 ? "" : option}
                                >
                                  {option}
                                </option>
                              )
                            )}
                          </select>
                          {formErrors.council_tax && (
                            <span className="error-message">
                              {formErrors.council_tax}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="form-group">
                        <label className="control-label col-sm-7 label-required">
                          Waste Arrange
                        </label>
                        <div className="col-sm-5">
                          <select
                            name="waste_arrange"
                            value={formData.waste_arrange}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.waste_arrange ? "error" : ""
                            }`}
                            required
                          >
                            {dropdownData.dropdown_options.map(
                              (option, index) => (
                                <option
                                  key={index}
                                  value={index === 0 ? "" : option}
                                >
                                  {option}
                                </option>
                              )
                            )}
                          </select>
                          {formErrors.waste_arrange && (
                            <span className="error-message">
                              {formErrors.waste_arrange}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="form-group">
                        <label className="control-label col-sm-7 label-required">
                          Waste Costs
                        </label>
                        <div className="col-sm-5">
                          <select
                            name="waste_costs"
                            value={formData.waste_costs}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.waste_costs ? "error" : ""
                            }`}
                            required
                          >
                            {dropdownData.dropdown_options.map(
                              (option, index) => (
                                <option
                                  key={index}
                                  value={index === 0 ? "" : option}
                                >
                                  {option}
                                </option>
                              )
                            )}
                          </select>
                          {formErrors.waste_costs && (
                            <span className="error-message">
                              {formErrors.waste_costs}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="form-group">
                        <label className="control-label col-sm-7 label-required">
                          Managing the Property
                        </label>
                        <div className="col-sm-5">
                          <select
                            name="managing_the_property"
                            value={formData.managing_the_property}
                            onChange={handleInputChange}
                            className={`form-control ${
                              formErrors.managing_the_property ? "error" : ""
                            }`}
                            required
                          >
                            {dropdownData.dropdown_options.map(
                              (option, index) => (
                                <option
                                  key={index}
                                  value={index === 0 ? "" : option}
                                >
                                  {option}
                                </option>
                              )
                            )}
                          </select>
                          {formErrors.managing_the_property && (
                            <span className="error-message">
                              {formErrors.managing_the_property}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Liabilities Section - Only for MTVH properties */}
                  {isMetropolitanHousing && liabilityTypes.length > 0 && (
                    <>
                      <div
                        className="report-section-header"
                        style={{
                          fontSize: "29px",
                          fontWeight: "600",
                          border: "none",
                          marginTop: "20px",
                        }}
                      >
                        Liabilities
                      </div>

                      <div className="row">
                        <div className="col-sm-4 mb-20 text-center">
                          <strong>Name</strong>
                        </div>
                        <div className="col-sm-4 mb-20 text-center">
                          <strong>Work</strong>
                        </div>
                        <div className="col-sm-4 mb-20 text-center">
                          <strong>Cost</strong>
                        </div>
                      </div>

                      {liabilityTypes.map((type) => {
                        const workFieldName = `liability[${type.id}][work]`;
                        const costFieldName = `liability[${type.id}][cost]`;
                        const existingLiability = propertyLiabilities[type.id] || {};
                        
                        return (
                          <div key={type.id} className="row p-10">
                            <div className="col-xs-4">{type.name}</div>

                            <div className="col-xs-4">
                              <div className="form-group">
                                <div className="col-xs-12">
                                  <select
                                    name={workFieldName}
                                    value={
                                      formData[workFieldName] ||
                                      (existingLiability.work ? String(existingLiability.work) : "") ||
                                      ""
                                    }
                                    onChange={handleInputChange}
                                    className="form-control"
                                  >
                                    <option value="">Please Select</option>
                                    {dropdownData.liabilityOptionsMap &&
                                      Object.entries(
                                        dropdownData.liabilityOptionsMap
                                      ).map(([id, name]) => (
                                        <option key={id} value={id}>
                                          {name}
                                        </option>
                                      ))}
                                  </select>
                                </div>
                              </div>
                            </div>

                            <div className="col-xs-4">
                              <div className="form-group">
                                <div className="col-xs-12">
                                  <select
                                    name={costFieldName}
                                    value={
                                      formData[costFieldName] ||
                                      (existingLiability.cost ? String(existingLiability.cost) : "") ||
                                      ""
                                    }
                                    onChange={handleInputChange}
                                    className="form-control"
                                  >
                                    <option value="">Please Select</option>
                                    {dropdownData.liabilityOptionsMap &&
                                      Object.entries(
                                        dropdownData.liabilityOptionsMap
                                      ).map(([id, name]) => (
                                        <option key={id} value={id}>
                                          {name}
                                        </option>
                                      ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}

              {!isMetropolitanHousing && (
                <>
                  <div
                    className="report-section-header"
                    style={{
                      fontSize: "29px",
                      fontWeight: "600",
                      border: "none",
                    }}
                  >
                    Key Information
                  </div>

                  <div className="row">
                    <div className="col-xs-12">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "15px",
                          marginBottom: "15px",
                        }}
                      >
                        <label
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            fontWeight: "bold",
                            textAlign: "right",
                            minWidth: "200px",
                          }}
                        >
                          Minimum Contract Period
                        </label>
                        <input
                          type="text"
                          name="npp_minimum_contract_period"
                          value={formData.npp_minimum_contract_period}
                          onChange={handleInputChange}
                          className="form-control"
                          style={{ width: "300px" }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-xs-12">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "15px",
                          marginBottom: "15px",
                        }}
                      >
                        <label
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            fontWeight: "bold",
                            textAlign: "right",
                            minWidth: "200px",
                          }}
                        >
                          Notice Period
                        </label>
                        <input
                          type="text"
                          name="npp_notice_period"
                          value={formData.npp_notice_period}
                          onChange={handleInputChange}
                          className="form-control"
                          style={{ width: "300px" }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-xs-12">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "15px",
                          marginBottom: "15px",
                        }}
                      >
                        <label
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            fontWeight: "bold",
                            textAlign: "right",
                            minWidth: "200px",
                          }}
                        >
                          Revenue Share
                        </label>
                        <input
                          type="text"
                          name="npp_revenue_share"
                          value={formData.npp_revenue_share}
                          onChange={handleInputChange}
                          className="form-control"
                          style={{ width: "300px" }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-xs-12">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "15px",
                          marginBottom: "15px",
                        }}
                      >
                        <label
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            fontWeight: "bold",
                            textAlign: "right",
                            minWidth: "200px",
                          }}
                        >
                          Public Liability
                        </label>
                        <input
                          type="text"
                          name="npp_public_liability"
                          value={formData.npp_public_liability}
                          onChange={handleInputChange}
                          className="form-control"
                          style={{ width: "300px" }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-xs-12">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "15px",
                          marginBottom: "15px",
                        }}
                      >
                        <label
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            fontWeight: "bold",
                            textAlign: "right",
                            minWidth: "200px",
                          }}
                        >
                          Employers Liability
                        </label>
                        <input
                          type="text"
                          name="npp_employers_liability"
                          value={formData.npp_employers_liability}
                          onChange={handleInputChange}
                          className="form-control"
                          style={{ width: "300px" }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-xs-12">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "15px",
                          marginBottom: "15px",
                        }}
                      >
                        <label
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            fontWeight: "bold",
                            textAlign: "right",
                            minWidth: "200px",
                          }}
                        >
                          Professional Indemnity
                        </label>
                        <input
                          type="text"
                          name="npp_professional_idemnity"
                          value={formData.npp_professional_idemnity}
                          onChange={handleInputChange}
                          className="form-control"
                          style={{ width: "300px" }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* New Liabilities Section */}
                  <div
                    className="report-section-header"
                    style={{
                      fontSize: "29px",
                      fontWeight: "600",
                      border: "none",
                    }}
                  >
                    Liabilities
                  </div>

                  <div className="row">
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Name</strong>
                    </div>
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Responsibility of completing works</strong>
                    </div>
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Responsibility of payment</strong>
                    </div>
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Estimated cost of work</strong>
                    </div>
                  </div>

                  {/* Myglobe Subscription */}
                  <div className="row p-10">
                    <div className="col-xs-3">Myglobe Subscription</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_myglobe_subscription_rm"
                        value={formData.npp_myglobe_subscription_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_myglobe_subscription_rp"
                        value={formData.npp_myglobe_subscription_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_myglobe_subscription_cow"
                        value={formData.npp_myglobe_subscription_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Management Fee */}
                  <div className="row p-10">
                    <div className="col-xs-3">Management fee</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_management_fee_rm"
                        value={formData.npp_management_fee_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_management_fee_rp"
                        value={formData.npp_management_fee_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_management_fee_cow"
                        value={formData.npp_management_fee_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Building Insurance */}
                  <div className="row p-10">
                    <div className="col-xs-3">Building Insurance</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_building_insurance_rm"
                        value={formData.npp_building_insurance_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_building_insurancee_rp"
                        value={formData.npp_building_insurancee_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_building_insurance_cow"
                        value={formData.npp_building_insurance_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Public Liability Insurance */}
                  <div className="row p-10">
                    <div className="col-xs-3">Public Liability Insurance</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_public_liability_insurance_rm"
                        value={formData.npp_public_liability_insurance_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_public_liability_insurance_rp"
                        value={formData.npp_public_liability_insurance_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_public_liability_insurance_cow"
                        value={formData.npp_public_liability_insurance_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* SIA Security */}
                  <div className="row p-10">
                    <div className="col-xs-3">
                      24/7 SIA Security if requested by The Owner
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_sia_security_rm"
                        value={formData.npp_sia_security_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_sia_security_rp"
                        value={formData.npp_sia_security_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_sia_security_cow"
                        value={formData.npp_sia_security_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Caretaker */}
                  <div className="row p-10">
                    <div className="col-xs-3">
                      24/7 Caretaker if requested by The Owner
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_catetaker_rm"
                        value={formData.npp_catetaker_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_catetaker_rp"
                        value={formData.npp_catetaker_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_catetaker_cow"
                        value={formData.npp_catetaker_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Access and Egress */}
                  <div className="row p-10">
                    <div className="col-xs-3">Access and Egress</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_access_egress_rm"
                        value={formData.npp_access_egress_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_access_egress_rp"
                        value={formData.npp_access_egress_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_access_egress_cow"
                        value={formData.npp_access_egress_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Monthly Inspections */}
                  <div className="row p-10">
                    <div className="col-xs-3">Monthly Inspections</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_monthly_inspections_rm"
                        value={formData.npp_monthly_inspections_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_monthly_inspections_rp"
                        value={formData.npp_monthly_inspections_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_monthly_inspections_cow"
                        value={formData.npp_monthly_inspections_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Weekly Inspections */}
                  <div className="row p-10">
                    <div className="col-xs-3">Weekly Inspections</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_weekly_inspections_rm"
                        value={formData.npp_weekly_inspections_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_weekly_inspections_rp"
                        value={formData.npp_weekly_inspections_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_weekly_inspections_cow"
                        value={formData.npp_weekly_inspections_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* HMO License */}
                  <div className="row p-10">
                    <div className="col-xs-3">HMO License</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_hmo_liscense_rm"
                        value={formData.npp_hmo_liscense_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_hmo_liscense_rp"
                        value={formData.npp_hmo_liscense_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_hmo_liscense_cow"
                        value={formData.npp_hmo_liscense_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Selective License */}
                  <div className="row p-10">
                    <div className="col-xs-3">Selective Licence</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_selective_license_rm"
                        value={formData.npp_selective_license_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_selective_license_rp"
                        value={formData.npp_selective_license_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_selective_license_cow"
                        value={formData.npp_selective_license_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Floor Plans */}
                  <div className="row p-10">
                    <div className="col-xs-3">Floor plans</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_floor_plans_rm"
                        value={formData.npp_floor_plans_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_floor_plans_rp"
                        value={formData.npp_floor_plans_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_floor_plans_cow"
                        value={formData.npp_floor_plans_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* VOA Application */}
                  <div className="row p-10">
                    <div className="col-xs-3">VOA Application</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_voa_application_rm"
                        value={formData.npp_voa_application_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_voa_application_rp"
                        value={formData.npp_voa_application_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_voa_application_cow"
                        value={formData.npp_voa_application_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Utility Liability Schedule */}
                  <div
                    className="report-section-header"
                    style={{
                      fontSize: "29px",
                      fontWeight: "600",
                      border: "none",
                    }}
                  >
                    Utility Liability Schedule
                  </div>

                  <div className="row">
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Name</strong>
                    </div>
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Responsibility of completing works</strong>
                    </div>
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Responsibility of payment</strong>
                    </div>
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Current Supplier and account number</strong>
                    </div>
                  </div>

                  {/* Gas */}
                  <div className="row p-10">
                    <div className="col-xs-3">Gas</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_gas_rm"
                        value={formData.npp_gas_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_gas_rp"
                        value={formData.npp_gas_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_gas_csan"
                        value={formData.npp_gas_csan}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Electricity */}
                  <div className="row p-10">
                    <div className="col-xs-3">Electricity</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_electricity_rm"
                        value={formData.npp_electricity_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_electricity_rp"
                        value={formData.npp_electricity_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_electricity_csan"
                        value={formData.npp_electricity_csan}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Water */}
                  <div className="row p-10">
                    <div className="col-xs-3">Water</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_water_rm"
                        value={formData.npp_water_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_water_rp"
                        value={formData.npp_water_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_water_csan"
                        value={formData.npp_water_csan}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Business Rates */}
                  <div className="row p-10">
                    <div className="col-xs-3">
                      Business Rates (where applicable)
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_business_rates_rm"
                        value={formData.npp_business_rates_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_business_rates_rp"
                        value={formData.npp_business_rates_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_business_rates_csan"
                        value={formData.npp_business_rates_csan}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Council Tax */}
                  <div className="row p-10">
                    <div className="col-xs-3">Council Tax</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_council_tax_rm"
                        value={formData.npp_council_tax_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_council_tax_rp"
                        value={formData.npp_council_tax_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_council_tax_csan"
                        value={formData.npp_council_tax_csan}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Bins */}
                  <div className="row p-10">
                    <div className="col-xs-3">Bins</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_bins_rm"
                        value={formData.npp_bins_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_bins_rp"
                        value={formData.npp_bins_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_bins_csan"
                        value={formData.npp_bins_csan}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Health and Safety Liability Schedule */}
                  <div
                    className="report-section-header"
                    style={{
                      fontSize: "29px",
                      fontWeight: "600",
                      border: "none",
                    }}
                  >
                    Health and Safety Liability Schedule
                  </div>

                  <div className="row">
                    <div className="col-sm-2 mb-20 text-center">
                      <strong>Name</strong>
                    </div>
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Responsibility of completing works</strong>
                    </div>
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Responsibility of payment</strong>
                    </div>
                    <div className="col-sm-2 mb-20 text-center">
                      <strong>Documentation Required</strong>
                    </div>
                    <div className="col-sm-2 mb-20 text-center">
                      <strong>Estimated cost of work</strong>
                    </div>
                  </div>

                  {/* Gas Safe */}
                  <div className="row p-10">
                    <div className="col-sm-2">Gas Safe</div>
                    <div className="col-sm-3">
                      <select
                        name="npp_gas_safe_rm"
                        value={formData.npp_gas_safe_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-3">
                      <select
                        name="npp_gas_safe_rp"
                        value={formData.npp_gas_safe_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <select
                        name="npp_gas_safe_dr"
                        value={formData.npp_gas_safe_dr}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.documentationOptions.map(
                          (option, index) => (
                            <option
                              key={index}
                              value={index === 0 ? "" : option}
                            >
                              {option}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <input
                        type="text"
                        name="npp_gas_safe_cow"
                        value={formData.npp_gas_safe_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* NIC */}
                  <div className="row p-10">
                    <div className="col-sm-2">NIC</div>
                    <div className="col-sm-3">
                      <select
                        name="npp_nic_rm"
                        value={formData.npp_nic_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-3">
                      <select
                        name="npp_nic_rp"
                        value={formData.npp_nic_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <select
                        name="npp_nic_dr"
                        value={formData.npp_nic_dr}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.documentationOptions.map(
                          (option, index) => (
                            <option
                              key={index}
                              value={index === 0 ? "" : option}
                            >
                              {option}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <input
                        type="text"
                        name="npp_nic_cow"
                        value={formData.npp_nic_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Legionella */}
                  <div className="row p-10">
                    <div className="col-sm-2">Legionella</div>
                    <div className="col-sm-3">
                      <select
                        name="npp_legionella_rm"
                        value={formData.npp_legionella_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-3">
                      <select
                        name="npp_legionella_rp"
                        value={formData.npp_legionella_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <select
                        name="npp_legionella_dr"
                        value={formData.npp_legionella_dr}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.documentationOptions.map(
                          (option, index) => (
                            <option
                              key={index}
                              value={index === 0 ? "" : option}
                            >
                              {option}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <input
                        type="text"
                        name="npp_legionella_cow"
                        value={formData.npp_legionella_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Asbestos */}
                  <div className="row p-10">
                    <div className="col-sm-2">Asbestos</div>
                    <div className="col-sm-3">
                      <select
                        name="npp_asbestos_rm"
                        value={formData.npp_asbestos_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-3">
                      <select
                        name="npp_asbestos_rp"
                        value={formData.npp_asbestos_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <select
                        name="npp_asbestos_dr"
                        value={formData.npp_asbestos_dr}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.documentationOptions.map(
                          (option, index) => (
                            <option
                              key={index}
                              value={index === 0 ? "" : option}
                            >
                              {option}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <input
                        type="text"
                        name="npp_asbestos_cow"
                        value={formData.npp_asbestos_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Fire Risk Assessment */}
                  <div className="row p-10">
                    <div className="col-sm-2">Fire Risk Assessment</div>
                    <div className="col-sm-3">
                      <select
                        name="npp_fire_risk_rm"
                        value={formData.npp_fire_risk_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-3">
                      <select
                        name="npp_fire_risk_rp"
                        value={formData.npp_fire_risk_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <select
                        name="npp_fire_risk_dr"
                        value={formData.npp_fire_risk_dr}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.documentationOptions.map(
                          (option, index) => (
                            <option
                              key={index}
                              value={index === 0 ? "" : option}
                            >
                              {option}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <input
                        type="text"
                        name="npp_fire_risk_cow"
                        value={formData.npp_fire_risk_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Fire Alarm */}
                  <div className="row p-10">
                    <div className="col-sm-2">Fire Alarm</div>
                    <div className="col-sm-3">
                      <select
                        name="npp_fire_alarm_rm"
                        value={formData.npp_fire_alarm_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-3">
                      <select
                        name="npp_fire_alarm_rp"
                        value={formData.npp_fire_alarm_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <select
                        name="npp_fire_alarm_dr"
                        value={formData.npp_fire_alarm_dr}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.documentationOptions.map(
                          (option, index) => (
                            <option
                              key={index}
                              value={index === 0 ? "" : option}
                            >
                              {option}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <input
                        type="text"
                        name="npp_fire_alarm_cow"
                        value={formData.npp_fire_alarm_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Emergency Lighting */}
                  <div className="row p-10">
                    <div className="col-sm-2">Emergency Lighting</div>
                    <div className="col-sm-3">
                      <select
                        name="npp_emergency_lighting_rm"
                        value={formData.npp_emergency_lighting_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-3">
                      <select
                        name="npp_emergency_lighting_rp"
                        value={formData.npp_emergency_lighting_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <select
                        name="npp_emergency_lighting_dr"
                        value={formData.npp_emergency_lighting_dr}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.documentationOptions.map(
                          (option, index) => (
                            <option
                              key={index}
                              value={index === 0 ? "" : option}
                            >
                              {option}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <input
                        type="text"
                        name="npp_emergency_lighting_cow"
                        value={formData.npp_emergency_lighting_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Fire Safety */}
                  <div className="row p-10">
                    <div className="col-sm-2">Fire Safety</div>
                    <div className="col-sm-3">
                      <select
                        name="npp_fire_safety_rm"
                        value={formData.npp_fire_safety_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-3">
                      <select
                        name="npp_fire_safety_rp"
                        value={formData.npp_fire_safety_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <select
                        name="npp_fire_safety_dr"
                        value={formData.npp_fire_safety_dr}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.documentationOptions.map(
                          (option, index) => (
                            <option
                              key={index}
                              value={index === 0 ? "" : option}
                            >
                              {option}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <input
                        type="text"
                        name="npp_fire_safety_cow"
                        value={formData.npp_fire_safety_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Fire Extinguisher */}
                  <div className="row p-10">
                    <div className="col-sm-2">Fire Extinguisher</div>
                    <div className="col-sm-3">
                      <select
                        name="npp_fire_extinguisher_rm"
                        value={formData.npp_fire_extinguisher_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-3">
                      <select
                        name="npp_fire_extinguisher_rp"
                        value={formData.npp_fire_extinguisher_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <select
                        name="npp_fire_extinguisher_dr"
                        value={formData.npp_fire_extinguisher_dr}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.documentationOptions.map(
                          (option, index) => (
                            <option
                              key={index}
                              value={index === 0 ? "" : option}
                            >
                              {option}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <input
                        type="text"
                        name="npp_fire_extinguisher_cow"
                        value={formData.npp_fire_extinguisher_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Maintenance Liability Schedule */}
                  <div
                    className="report-section-header"
                    style={{
                      fontSize: "29px",
                      fontWeight: "600",
                      border: "none",
                    }}
                  >
                    Maintenance Liability Schedule
                  </div>

                  <div className="row">
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Name</strong>
                    </div>
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Responsibility of completing works</strong>
                    </div>
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Responsibility of payment</strong>
                    </div>
                    <div className="col-sm-3 mb-20 text-center">
                      <strong>Estimated cost of work</strong>
                    </div>
                  </div>

                  {/* Initial Fitout */}
                  <div className="row p-10">
                    <div className="col-xs-3">Initial Fitout</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_initial_fitout_rm"
                        value={formData.npp_initial_fitout_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_initial_fitout_rp"
                        value={formData.npp_initial_fitout_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_initial_fitout_cow"
                        value={formData.npp_initial_fitout_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Locks and Keys */}
                  <div className="row p-10">
                    <div className="col-xs-3">Locks and Keys</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_locks_keys_rm"
                        value={formData.npp_locks_keys_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_locks_keys_rp"
                        value={formData.npp_locks_keys_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_locks_keys_cow"
                        value={formData.npp_locks_keys_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Ongoing Internal Maintenance */}
                  <div className="row p-10">
                    <div className="col-xs-3">Ongoing Internal Maintenance</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_ongoing_internal_maintenance_rm"
                        value={formData.npp_ongoing_internal_maintenance_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_ongoing_internal_maintenance_rp"
                        value={formData.npp_ongoing_internal_maintenance_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_ongoing_internal_maintenance_cow"
                        value={formData.npp_ongoing_internal_maintenance_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Boiler Repair */}
                  <div className="row p-10">
                    <div className="col-xs-3">Boiler Repair</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_boiler_repair_rm"
                        value={formData.npp_boiler_repair_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_boiler_repair_rp"
                        value={formData.npp_boiler_repair_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_boiler_repair_cow"
                        value={formData.npp_boiler_repair_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Ongoing External Maintenance */}
                  <div className="row p-10">
                    <div className="col-xs-3">Ongoing External Maintenance</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_ongoing_external_maintenance_rm"
                        value={formData.npp_ongoing_external_maintenance_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_ongoing_external_maintenance_rp"
                        value={formData.npp_ongoing_external_maintenance_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_ongoing_external_maintenance_cow"
                        value={formData.npp_ongoing_external_maintenance_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Cleaning Areas */}
                  <div className="row p-10">
                    <div className="col-xs-3">Cleaning Areas</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_cleaning_areas_rm"
                        value={formData.npp_cleaning_areas_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_cleaning_areas_rp"
                        value={formData.npp_cleaning_areas_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_cleaning_areas_cow"
                        value={formData.npp_cleaning_areas_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Emergency Repairs */}
                  <div className="row p-10">
                    <div className="col-xs-3">Emergency Repairs</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_emergency_repairs_rm"
                        value={formData.npp_emergency_repairs_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_emergency_repairs_rp"
                        value={formData.npp_emergency_repairs_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_emergency_repairs_cow"
                        value={formData.npp_emergency_repairs_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Gardening */}
                  <div className="row p-10">
                    <div className="col-xs-3">Gardening</div>
                    <div className="col-xs-3">
                      <select
                        name="npp_gardening_rm"
                        value={formData.npp_gardening_rm}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <select
                        name="npp_gardening_rp"
                        value={formData.npp_gardening_rp}
                        onChange={handleInputChange}
                        className="form-control"
                      >
                        {dropdownData.liabilityOptions.map((option, index) => (
                          <option key={index} value={index === 0 ? "" : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xs-3">
                      <input
                        type="text"
                        name="npp_gardening_cow"
                        value={formData.npp_gardening_cow}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Submit Button */}
              <div className="form-group text-right">
                <div className="col-sm-12">
                  <button
                    style={{ backgroundColor: "#17264B" }}
                    type="button"
                    className="btn btn-head btn-standard"
                    disabled={loading}
                    onClick={handleSubmit}
                  >
                    {loading ? "Updating..." : "Update Property Proposal"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyProposalEdit;
